/**
 * services/projectConsultants.js — subconsultant candidates/award flow for a project.
 *
 * Backed by the existing Airtable `Project Consultants` table (tblJs5ExcClazu7MU) — one row per
 * (Project, Consultant organization, Consultant Service). This table also carries the full RFP /
 * proposal / subconsultant-agreement paperwork (see its `RFPs` link) — none of that is touched here.
 *
 * The manually-added lifecycle field is `Status` (Single select: Candidate | Awarded). It's
 * independent of the RFP paperwork: a row starts as a Candidate when added from Team, and "Award"
 * flips it to Awarded, which is what makes it show as a real subconsultant branch with people
 * assignable (see services/team.js#groupTeamRows). Mirrors the Bid Campaigns candidate → award
 * pattern (docs/CONSTRUCTION_BIDS.md).
 */

import { apiGetAll, apiPost, apiPatch, apiDelete } from './_api.js'

const TBL_PROJECT_CONSULTANTS = 'tblJs5ExcClazu7MU'

// The firm's controlled vocabulary for Project Consultants' own Consultant Service field
// (a different, smaller list than the one on Consultant RFPs — this is the one we write to).
export const CONSULTANT_SERVICE_OPTIONS = [
  'Interior Designer', 'Design Architect', 'Landscape Architect', 'Civil Engineer',
  'Structural Engineer', 'Mechanical Engineer', 'Electrical Engineer',
  'Mechanical and Electrical Engineer', 'Energy Consultant', 'Soils Engineer',
  'Site Surveyor', 'Hazardous Materials Surveyor', 'Permit Expediter',
]

function getFirst(v) { return Array.isArray(v) ? (v[0] || null) : (v || null) }

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim) instead
   of throwing — surface it so a bad write shows up as a real error, not a silent no-op that looks
   like success. */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

/** Every Project Consultants row for this project, server-filtered by the Project Record UID lookup. */
export function getProjectConsultants(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  return apiGetAll(TBL_PROJECT_CONSULTANTS, 'filterByFormula=' + encodeURIComponent(formula))
    .then(rows => (rows || []).map(r => ({
      id: r.id,
      orgId: getFirst(r.fields['Consultant']),
      consultantService: r.fields['Consultant Service'] || '',
      status: r.fields['Status'] || 'Candidate', // rows created before the field existed default to Candidate
    })))
}

export async function addCandidateConsultant(projectId, orgId, consultantService) {
  return throwOnError(await apiPost(TBL_PROJECT_CONSULTANTS, {
    Project: [projectId],
    Consultant: [orgId],
    'Consultant Service': consultantService,
    Status: 'Candidate',
  }), 'candidate')
}

export async function awardConsultant(rowId) {
  return throwOnError(await apiPatch(TBL_PROJECT_CONSULTANTS, rowId, { Status: 'Awarded' }), 'award')
}

export async function removeConsultantRow(rowId) {
  return throwOnError(await apiDelete(TBL_PROJECT_CONSULTANTS, rowId), 'subconsultant')
}
